import { SITE, ORGANIZATION_SCHEMA, serviceSchema, webPageSchema, breadcrumbSchema } from "./site";
import { HOME_FAQ_ITEMS } from "../content/faq";

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  url: `${SITE.url}/`,
  name: SITE.name,
  publisher: { "@id": `${SITE.url}/#organization` },
};

const crumbs = (name, path) => breadcrumbSchema([{ name: "Home", path: "/" }, { name, path }]);

const servicePage = ({ path, title, ogTitle, description, name, serviceType, crumb }) => ({
  path,
  title,
  ogTitle,
  description,
  schemas: [
    serviceSchema({ path, name, description, serviceType }),
    webPageSchema({ path, title, description }),
    crumbs(crumb || name, path),
  ],
});

const faqPageSchema = (path, items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE.url}${path === "/" ? "/" : path}#faqpage`,
  url: `${SITE.url}${path === "/" ? "/" : path}`,
  mainEntity: items.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a.join(" ") },
  })),
});

export const PAGES = [
  {
    path: "/",
    title: "The Formulation House — Cosmetic, Ayurvedic & Nutraceutical Formulation Studio",
    ogTitle: "The Formulation House — Build Exceptional Products. Launch with Confidence.",
    description:
      "The Formulation House is a doctor-led formulation & product development studio for beauty, wellness and Ayurvedic brands — 100+ formulas across cosmetics, Ayurveda and nutraceuticals, from first idea to market-ready product.",
    schemas: [
      ORGANIZATION_SCHEMA,
      WEBSITE_SCHEMA,
      webPageSchema({ path: "/", title: "The Formulation House", description: "Doctor-led formulation and product development studio.", type: "WebPage" }),
      faqPageSchema("/", HOME_FAQ_ITEMS),
    ],
  },
  {
    path: "/about",
    title: "About The Formulation House — Doctor-Led Formulation & Product Development",
    description:
      "Meet The Formulation House: a doctor-led formulation studio with 10+ years of experience, 100+ formulations and 20+ product categories across cosmetics, Ayurveda and nutraceuticals.",
    schemas: [ORGANIZATION_SCHEMA, webPageSchema({ path: "/about", title: "About The Formulation House", description: "Our expertise, approach and product development capabilities.", type: "AboutPage" }), crumbs("About", "/about")],
  },
  servicePage({
    path: "/services",
    title: "Formulation & Product Development Services | The Formulation House",
    description:
      "End-to-end formulation services: custom formula development, stock formulas, manufacturing coordination, packaging guidance, testing and regulatory support for beauty, wellness and Ayurvedic brands.",
    name: "Formulation & Product Development Services",
    serviceType: "Product development",
    crumb: "Services",
  }),
  servicePage({
    path: "/cosmetic-formulation-development",
    title: "Cosmetic Formulation Development Services | The Formulation House",
    description:
      "Custom cosmetic and personal-care formulation development — creams, cleansers, serums, body care and more — built on modern actives, elegant textures and proven stability by a doctor-led team.",
    name: "Cosmetic Formulation Development",
    serviceType: "Cosmetic formulation",
  }),
  servicePage({
    path: "/skincare-formulation-development",
    title: "Skincare Formulation & Product Development | The Formulation House",
    description:
      "Skincare formulation development for serums, moisturisers, cleansers, masks and SPF-ready bases — actives-led, stability-tested and developed for Indian and export markets.",
    name: "Skincare Formulation Development",
    serviceType: "Skincare formulation",
  }),
  servicePage({
    path: "/haircare-formulation-development",
    title: "Haircare Formulation & Product Development | The Formulation House",
    description:
      "Scalp-first haircare formulation development: shampoos, conditioners, hair oils, serums, masks and scalp treatments — sulphate-free, herbal and performance-led systems.",
    name: "Haircare Formulation Development",
    serviceType: "Haircare formulation",
  }),
  servicePage({
    path: "/wellness-product-development",
    title: "Wellness Oils & Wellness Product Development | The Formulation House",
    description:
      "Wellness product development for aromatherapy oils, massage oils, body oils, balms, wellness teas and Ayurveda-inspired rituals — developed with Ayurvedic doctors and formulation experts.",
    name: "Wellness Product Development",
    serviceType: "Wellness product development",
  }),
  servicePage({
    path: "/private-label-cosmetics-india",
    title: "Private Label Cosmetics & Contract Product Development in India",
    description:
      "Private-label and contract product-development support for brands in India: proven ready-to-launch formulas, custom development, licensed manufacturing partners and export-ready documentation.",
    name: "Private Label Cosmetics India",
    serviceType: "Private label product development",
    crumb: "Private Label Cosmetics India",
  }),
  servicePage({
    path: "/cosmetic-manufacturing-support",
    title: "Cosmetic Manufacturing Support & Scale-Up | The Formulation House",
    description:
      "Manufacturing support for cosmetic and wellness brands: scale-up, tech transfer, batch production coordination with licensed facilities, packaging compatibility and production readiness.",
    name: "Cosmetic Manufacturing Support",
    serviceType: "Manufacturing coordination",
  }),
  servicePage({
    path: "/regulatory-testing-support",
    title: "Regulatory, Testing & Compliance Support for Cosmetics | The Formulation House",
    description:
      "Testing coordination, stability and safety documentation, label and claims review, regulatory guidance and quality documentation for cosmetic, Ayurvedic and nutraceutical products.",
    name: "Regulatory & Testing Support",
    serviceType: "Regulatory compliance support",
  }),
  {
    path: "/faq",
    title: "FAQ — Manufacturing, MOQ, NDA & Formula Ownership | The Formulation House",
    description:
      "Answers to common questions about working with The Formulation House: manufacturing partners, exclusive formulations, minimum order quantity, packaging, reformulation, NDAs, IP ownership and ingredient sourcing.",
    schemas: [
      webPageSchema({ path: "/faq", title: "FAQ — The Formulation House", description: "Answers to common questions about working with The Formulation House.", type: "FAQPage" }),
      crumbs("FAQ", "/faq"),
    ],
  },
  {
    path: "/contact",
    title: "Contact The Formulation House — Book a Free Formulation Call",
    description:
      "Tell us about the product you want to build. Book a free discovery call with The Formulation House for cosmetic, Ayurvedic, wellness and nutraceutical product development.",
    schemas: [webPageSchema({ path: "/contact", title: "Contact The Formulation House", description: "Book a free discovery call.", type: "ContactPage" }), crumbs("Contact", "/contact")],
  },
];

export const findPage = (pathname) => PAGES.find((p) => p.path === pathname.replace(/\/+$/, "") || (p.path === "/" && pathname === "/"));
